<template>
    <section class="fly-detailed-section">
        <div v-if="flyInformation" class="fly-detailed-table">
            <div class="container mx-auto px-4 xl:px-0">
                <div class="heading">
                    <h1 class="title">
                        {{ flyInformation.airline.name }} ({{ flyInformation.airline.iata }})
                        {{ flyInformation.flightNumber }}
                    </h1>
                    <div class="journey">
                        {{ flyInformation.departureAirport.iata }}
                        <NuxtIcon name="ri:arrow-right-line" class="mx-3" />
                        {{ flyInformation.arrivalAirport.iata }}
                    </div>
                    <div
                        class="status"
                        :class="{
                            success: !getDelay(flyInformation),
                            delay: getDelay(flyInformation),
                        }"
                    >
                        {{ flyInformation.statusLabel === 'Active' ? 'Departed' : flyInformation.statusLabel }}
                        <span v-if="flyInformation.status != 'C'" class="schedule">{{
                            getDelay(flyInformation) ? 'Delayed by ' + getDelay(flyInformation) + ' minutes' : 'On Time'
                        }}</span>
                    </div>
                </div>
            </div>
            <div class="flight-body">
                <div class="container mx-auto px-4 xl:px-0">
                    <div class="flight-information">
                        <div class="departure">
                            <h3 class="title">
                                Departure: {{ flyInformation.departureAirport.city }} ({{
                                    flyInformation.departureAirport.iata
                                }})
                                <span class="!hidden lg:!inline"> on </span>
                                <span> {{ formatFriendlyDate(flyInformation.departureTime) }} </span>
                            </h3>
                            <div class="info">
                                <div class="scheduled pb-4 lg:pb-0 lg:pr-14">
                                    <h6>Scheduled:</h6>
                                    <div>
                                        {{
                                            timeFormat(
                                                flyInformation.departureTime,
                                                flyInformation.departureAirport.timeZoneRegionName
                                            )
                                        }}
                                    </div>
                                </div>
                                <div class="terminal pb-4 lg:pb-0 lg:pr-20">
                                    <h6>Terminal:</h6>
                                    <div class="font-weight-light">
                                        {{
                                            flyInformation.departureTerminal ? flyInformation.departureTerminal : 'N/A'
                                        }}
                                    </div>
                                </div>
                                <div class="gate">
                                    <h6>Gate:</h6>
                                    <div>
                                        {{ flyInformation.departureGate ? flyInformation.departureGate : 'N/A' }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="arrival">
                            <h3 class="title">
                                Arrival: {{ flyInformation.arrivalAirport.city }} ({{
                                    flyInformation.arrivalAirport.iata
                                }})
                                <span class="!hidden lg:!inline"> on </span>
                                <span>{{ formatFriendlyDate(flyInformation.arrivalTime) }} </span>
                            </h3>
                            <div class="info">
                                <div class="scheduled pb-4 lg:pb-0 lg:pr-14">
                                    <h6>Scheduled:</h6>
                                    <div>
                                        {{
                                            timeFormat(
                                                flyInformation.arrivalTime,
                                                flyInformation.arrivalAirport.timeZoneRegionName
                                            )
                                        }}
                                    </div>
                                </div>
                                <div class="terminal pb-4 lg:pb-0 lg:pr-20">
                                    <h6>Terminal:</h6>
                                    <div>
                                        {{ flyInformation.arrivalTerminal ? flyInformation.arrivalTerminal : 'N/A' }}
                                    </div>
                                </div>
                                <div class="gate">
                                    <h6>Gate:</h6>
                                    <div>
                                        {{ flyInformation.arrivalGate ? flyInformation.arrivalGate : 'N/A' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flight-details">
                        <h3 class="title">Flight Details</h3>
                        <div class="info">
                            <div class="flight-time pb-4 lg:pb-0 lg:pr-14">
                                <h6>Total Flight Time:</h6>
                                <div>{{ flightDuration }}</div>
                            </div>
                            <div class="airplane">
                                <h6>Airplane:</h6>
                                <div>
                                    {{ flyInformation.airline.name }} ({{ flyInformation.airline.iata }})
                                    {{ flyInformation.flightNumber }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="container mx-auto px-4 xl:px-0">
            <p>No fly information found</p>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { IDetailedFlightInfo } from '~/types/FlyStatus';
import type { ComputedRef } from 'vue';

const route: any = useRoute();

const { data }: any = await useAsyncData(
    async () =>
        await $privateApi(`flight/${route.params.result}`, {
            method: 'GET',
        }),
    { lazy: true }
);

const flyInformation: ComputedRef<IDetailedFlightInfo> = computed(() => (data.value ? data.value.data : false));

const getUTCDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

const departureTime = computed(() => {
    let time = flyInformation.value.departureTime;
    const actualTime = flyInformation.value.actualGateDeparture || flyInformation.value.actualRunwayDeparture;
    const estimatedTime = flyInformation.value.estimatedGateDeparture || flyInformation.value.estimatedRunwayDeparture;

    if (actualTime) {
        time = actualTime;
    } else if (estimatedTime) {
        time = estimatedTime;
    }
    return getUTCDate(time);
});

const arrivalTime = computed(() => {
    let time = flyInformation.value.arrivalTime;
    const actualTime = flyInformation.value.actualGateArrival || flyInformation.value.actualRunwayArrival;
    const estimatedTime = flyInformation.value.estimatedGateArrival || flyInformation.value.estimatedRunwayArrival;

    if (actualTime) {
        time = actualTime;
    } else if (estimatedTime) {
        time = estimatedTime;
    }
    return getUTCDate(time);
});

const flightDuration = computed(() => {
    const diff = arrivalTime.value.getTime() - departureTime.value.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    let duration = '';
    if (hours > 0) {
        duration += hours + 'h ';
    }
    duration += remainingMinutes + 'm';

    return duration;
});

const getDelay = (e: IDetailedFlightInfo) => {
    if (e.status == 'L') {
        return e.arrivalGateDelayMinutes;
    }
    return e.departureGateDelayMinutes;
};

const formatFriendlyDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const timeFormat = (time: string, tz: string) => {
    if (!time) {
        return null;
    }

    try {
        const date = new Date(time);
        const options: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: tz,
        };

        const timeStr = date.toLocaleTimeString('en-US', options);
        return `${timeStr} ${tz}`;
    } catch (error) {
        console.error('Error formatting time:', error);
        return time;
    }
};

const pageTitle = computed(() => {
    const info = flyInformation.value;
    if (!info) return 'Flight Status Tracker';

    const { airline, flightNumber, departureAirport, arrivalAirport } = info;

    return `Flight ${airline.iata}${flightNumber} ${airline.name} ${departureAirport.iata} to ${arrivalAirport.iata} Flight Status Tracker`;
});

useHead({
    title: pageTitle.value,
});
</script>

<style scoped lang="postcss">
.fly-detailed-section {
    .fly-detailed-table {
        @apply text-text-primary-asphalt;
        .heading {
            @apply py-4 md:pt-12 md:pb-16;
            .title {
                @apply text-xl md:text-[2.5rem] font-semibold leading-8 lg:text-4xl lg:leading-[3rem];
            }
            .journey {
                @apply text-base md:text-2xl  mt-4 font-normal;
            }
            .status {
                @apply text-base mt-4;
                &.success {
                    @apply text-status-success-text;
                }
                &.delay {
                    @apply text-status-error-red-100;
                }
            }
        }
        .title {
            @apply text-base font-semibold leading-6 lg:text-2xl lg:leading-8;

            span {
                @apply block font-normal pt-2 lg:inline;
            }
        }
        .info {
            @apply text-base pt-4 lg:flex;
            h6 {
                @apply text-base font-semibold mb-2 lg:mb-4;
            }
        }
        .flight-information {
            @apply py-4 md:py-6 border-b-[#dee2e6] border-b border-solid;
            .departure {
                @apply pb-6 border-b-[#dee2e6] border-b border-solid lg:border-b-[none];
            }
            .arrival {
                @apply pt-6;
            }
        }
        .flight-details {
            @apply py-4 md:py-6;
        }
    }
}

.flight-body {
    @apply bg-surface-brand-super-light py-2 lg:py-8 relative;
}
</style>
